import React from 'react'
import axios from 'axios'
import {
  Button,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  Snackbar
} from '@material-ui/core'
import {
  CloudUpload as CloudUploadIcon,
  PictureAsPdf,
  Description as DescriptionIcon,
  InsertDriveFile,
  Image as ImageIcon,
  Delete as DeleteIcon,
  GetApp as GetAppIcon,
  Send as SendIcon,
  EmojiObjects as LightbulbIcon,
  Tune as TuneIcon,
  Visibility as VisibilityIcon
} from '@material-ui/icons'
import DocumentUploadModal from './DocumentUploadModal'
import TakeoffRequestModal from './TakeoffRequestModal'
import ImportPreviewModal from './ImportPreviewModal'
import PlanPageSelector from './PlanPageSelector'
import VisualReviewModal from './VisualReviewModal'
import ExtractionRulesDrawer from './ExtractionRulesDrawer'

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch (e) {
    return ''
  }
}

const iconForFileType = (fileType) => {
  switch ((fileType || '').toLowerCase()) {
    case 'pdf':
      return <PictureAsPdf style={{ color: '#d32f2f', fontSize: 32 }} />
    case 'xlsx':
    case 'xls':
    case 'csv':
      return <DescriptionIcon style={{ color: '#2e7d32', fontSize: 32 }} />
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return <ImageIcon style={{ color: '#1976d2', fontSize: 32 }} />
    default:
      return <InsertDriveFile style={{ color: '#757575', fontSize: 32 }} />
  }
}

const parseStatusChip = (status) => {
  switch (status) {
    case 'parsed':
      return { label: 'Parsed', style: { backgroundColor: '#c8e6c9', color: '#2e7d32' } }
    case 'classified':
      return { label: 'Plan Set — Select Pages', style: { backgroundColor: '#e3f2fd', color: '#1565c0' } }
    case 'failed':
      return { label: 'Parse failed', style: { backgroundColor: '#ffcdd2', color: '#c62828' } }
    case 'unsupported':
      return { label: 'Unsupported', style: { backgroundColor: '#eeeeee', color: '#616161' } }
    case 'pending':
      return { label: 'Parsing…', style: { backgroundColor: '#fff9c4', color: '#f57f17' } }
    case 'not_attempted':
    default:
      return { label: 'Not parsed', style: { backgroundColor: '#f5f5f5', color: '#757575' } }
  }
}

const cardPanelStyle = {
  backgroundColor: '#fff',
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  padding: 16
}

const DocumentsSection = ({ projectId, project, url, onRefresh, onTriggerUpload }) => {
  const [documents, setDocuments] = React.useState(
    (project && Array.isArray(project.documents)) ? project.documents : []
  )
  const [loading, setLoading] = React.useState(false)
  const [showUploadModal, setShowUploadModal] = React.useState(false)
  const [importDoc, setImportDoc] = React.useState(null) // { _id, parseResult }
  const [pageSelectorDoc, setPageSelectorDoc] = React.useState(null) // doc with classified parseResult
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '' })
  const [takeoffDoc, setTakeoffDoc] = React.useState(null)
  const [rulesDrawer, setRulesDrawer] = React.useState({ open: false, mode: 'manager' })
  const [viewPagesDoc, setViewPagesDoc] = React.useState(null) // doc to view page images for

  const fetchDocuments = React.useCallback(() => {
    if (!projectId) return
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents`).then(res => {
      setDocuments(Array.isArray(res.data) ? res.data : [])
      setLoading(false)
    }).catch(err => {
      console.error('[DocumentsSection] fetch error:', err)
      setLoading(false)
    })
  }, [projectId, url])

  React.useEffect(() => {
    if (project && Array.isArray(project.documents)) {
      setDocuments(project.documents)
    } else {
      fetchDocuments()
    }
  }, [projectId, project])

  // Listen for socket events to auto-refresh when extraction completes
  React.useEffect(() => {
    if (!projectId) return
    try {
      const { subscribe } = require('../../SocketIo/Socket')
      const unsub = subscribe(`doc_upload_${projectId}`, (data) => {
        if (data && data.stage === 'complete' && data.parseStatus === 'parsed') {
          fetchDocuments()
          if (onRefresh) onRefresh()
        }
      })
      return unsub
    } catch (e) { /* socket not available */ }
  }, [projectId, fetchDocuments, onRefresh])

  const handleUploaded = (newDoc) => {
    if (newDoc && newDoc._id) {
      setDocuments(prev => [...prev, newDoc])
    } else {
      fetchDocuments()
    }
    if (onRefresh) onRefresh()
  }

  const handleDelete = (doc) => {
    if (!doc || !doc._id) return
    if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return
    axios.delete(`${url}/api/bid-estimation/projects/${projectId}/documents/${doc._id}`).then(() => {
      setDocuments(prev => prev.filter(d => d._id !== doc._id))
      setSnackbar({ open: true, message: 'Document deleted' })
      if (onRefresh) onRefresh()
    }).catch(err => {
      console.error('[DocumentsSection] delete error:', err)
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to delete document'
      })
    })
  }

  const handleImportClick = (doc) => {
    if (doc.parseResult) {
      setImportDoc({ _id: doc._id, parseResult: doc.parseResult })
      return
    }
    // Fallback: fetch the parse result from the server
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents/${doc._id}/parse-result`).then(res => {
      setImportDoc({ _id: doc._id, parseResult: res.data.parseResult })
    }).catch(err => {
      console.error('[DocumentsSection] parse-result fetch error:', err)
      setSnackbar({ open: true, message: 'Could not load parse result' })
    })
  }

  const handleImported = (count) => {
    setSnackbar({ open: true, message: `Imported ${count} item${count === 1 ? '' : 's'}` })
    if (onRefresh) onRefresh()
  }

  const handleQuickExtract = async (docId, pages) => {
    try {
      await axios.post(
        `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/parse-pages`,
        { pages }
      )
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to start extraction' })
    }
  }

  const handlePagesParsed = (updatedDoc) => {
    // Replace the doc in local state with the updated version (now parseStatus='parsed')
    if (updatedDoc && updatedDoc._id) {
      setDocuments(prev => prev.map(d => d._id === updatedDoc._id ? updatedDoc : d))
    } else {
      fetchDocuments()
    }
    setPageSelectorDoc(null)
    setSnackbar({ open: true, message: 'Pages extracted! Click "Import Items" to add them.' })
    if (onRefresh) onRefresh()
  }

  return (
    <div style={cardPanelStyle}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12
      }}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Documents
          {documents.length > 0 && (
            <span style={{ marginLeft: 8, color: '#757575', fontSize: 14, fontWeight: 400 }}>
              ({documents.length})
            </span>
          )}
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Tooltip title="Extraction Rules">
            <IconButton size="small" onClick={() => setRulesDrawer({ open: true, mode: 'manager' })}>
              <TuneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<CloudUploadIcon />}
            onClick={() => onTriggerUpload ? onTriggerUpload() : setShowUploadModal(true)}
            style={{ textTransform: 'none' }}
          >
            Upload Document
          </Button>
        </div>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <CircularProgress size={28} />
        </div>
      )}

      {!loading && documents.length === 0 && (
        <div style={{
          padding: '32px 16px',
          textAlign: 'center',
          color: '#757575',
          border: '1px dashed #e0e0e0',
          borderRadius: 4
        }}>
          <InsertDriveFile style={{ fontSize: 40, color: '#bdbdbd', marginBottom: 8 }} />
          <Typography variant="body2">
            No documents uploaded yet. Upload PDFs, spreadsheets, or images to reference them for this project.
          </Typography>
        </div>
      )}

      {!loading && documents.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {documents.map(doc => {
            const chip = parseStatusChip(doc.parseStatus)
            return (
              <div
                key={doc._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 12,
                  border: '1px solid #eeeeee',
                  borderRadius: 4,
                  backgroundColor: '#fafafa'
                }}
              >
                {iconForFileType(doc.fileType)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a
                    href={doc.s3Url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#1565c0',
                      fontWeight: 600,
                      textDecoration: 'none',
                      fontSize: 14,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block'
                    }}
                  >
                    {doc.name}
                  </a>
                  <div style={{
                    display: 'flex',
                    gap: 12,
                    marginTop: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}>
                    <Typography variant="caption" color="textSecondary">
                      {formatBytes(doc.sizeBytes)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatDate(doc.uploadedAt || doc.createdAt)}
                    </Typography>
                    <Chip
                      size="small"
                      label={chip.label}
                      style={{ ...chip.style, height: 20, fontSize: 11 }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {doc.parseStatus === 'classified' && doc.parseResult && doc.parseResult.sheetIndex && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="textSecondary" style={{ fontSize: 12 }}>
                        {doc.parseResult.sheetIndex.length} pages —{' '}
                        {doc.parseResult.sheetIndex.filter(s => s.hasSchedule).length} schedules detected
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        style={{ textTransform: 'none', fontSize: 11, padding: '2px 10px' }}
                        disabled={doc.parseResult.sheetIndex.filter(s => s.hasSchedule).length === 0}
                        onClick={() => {
                          const schedulePages = doc.parseResult.sheetIndex
                            .filter(s => s.hasSchedule)
                            .map(s => s.page)
                          handleQuickExtract(doc._id, schedulePages)
                        }}
                      >
                        Extract Schedules ({doc.parseResult.sheetIndex.filter(s => s.hasSchedule).length})
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        style={{ textTransform: 'none', fontSize: 11, padding: '2px 10px' }}
                        onClick={() => setPageSelectorDoc(doc)}
                      >
                        Review & Select Pages
                      </Button>
                    </div>
                  )}
                  {doc.parseStatus === 'parsed' && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<GetAppIcon />}
                        onClick={() => handleImportClick(doc)}
                        style={{ textTransform: 'none' }}
                      >
                        Import Items
                      </Button>
                      {doc.parseResult && doc.parseResult.sheetIndex && (
                        <Tooltip title="View Page Images">
                          <IconButton
                            size="small"
                            onClick={() => setViewPagesDoc(doc)}
                          >
                            <VisibilityIcon fontSize="small" style={{ color: '#1565c0' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Extraction Rules">
                        <IconButton
                          size="small"
                          onClick={() => setRulesDrawer({ open: true, mode: 'manager' })}
                        >
                          <LightbulbIcon fontSize="small" style={{ color: '#f57f17' }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {doc.category === 'plans' && (
                    <Tooltip title="Request Takeoff">
                      <IconButton size="small" onClick={() => setTakeoffDoc(doc)}>
                        <SendIcon fontSize="small" style={{ color: '#1565c0' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(doc)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <DocumentUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploaded={handleUploaded}
        projectId={projectId}
        url={url}
      />

      {importDoc && (
        <ImportPreviewModal
          open={Boolean(importDoc)}
          onClose={() => setImportDoc(null)}
          onImported={(count) => {
            setImportDoc(null)
            handleImported(count)
          }}
          projectId={projectId}
          docId={importDoc._id}
          parseResult={importDoc.parseResult}
          project={project}
          url={url}
        />
      )}

      {pageSelectorDoc && (
        <PlanPageSelector
          open={Boolean(pageSelectorDoc)}
          onClose={() => setPageSelectorDoc(null)}
          doc={pageSelectorDoc}
          projectId={projectId}
          url={url}
          onParsed={handlePagesParsed}
        />
      )}

      <TakeoffRequestModal
        open={!!takeoffDoc}
        onClose={(action, count) => {
          setTakeoffDoc(null)
          if (action === 'sent') {
            setSnackbar({ open: true, message: `Takeoff requests sent to ${count} vendor${count !== 1 ? 's' : ''}` })
          }
        }}
        doc={takeoffDoc}
        projectId={projectId}
        project={project}
        url={url}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {viewPagesDoc && viewPagesDoc.parseResult && viewPagesDoc.parseResult.sheetIndex && (
        <VisualReviewModal
          open={Boolean(viewPagesDoc)}
          onClose={() => setViewPagesDoc(null)}
          sheetIndex={viewPagesDoc.parseResult.sheetIndex}
          selected={new Set()}
          projectId={projectId}
          docId={viewPagesDoc._id}
          url={url}
          readOnly
        />
      )}

      <ExtractionRulesDrawer
        open={rulesDrawer.open}
        onClose={() => setRulesDrawer({ open: false, mode: 'manager' })}
        url={url}
        mode={rulesDrawer.mode}
        projectId={rulesDrawer.projectId || projectId}
        docId={rulesDrawer.docId}
        page={rulesDrawer.page}
        discipline={rulesDrawer.discipline}
        extractedItems={rulesDrawer.extractedItems}
      />
    </div>
  )
}

export default DocumentsSection
